import React, { createContext, useReducer, useEffect } from 'react';
import { AUTH_STORAGE_KEYS, USER_ROLES } from '../utils/constants';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: null,
  refreshToken: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  loginModalOpen: false,
  registerModalOpen: false
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
        error: null
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false
      };
    case 'LOGOUT':
      return {
        ...initialState
      };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'TOKEN_REFRESHED':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken
      };
    case 'TOGGLE_LOGIN_MODAL':
      return { 
        ...state, 
        loginModalOpen: action.payload,
        registerModalOpen: false  // Close register modal if opening login
      };
    case 'TOGGLE_REGISTER_MODAL':
      return { 
        ...state, 
        registerModalOpen: action.payload,
        loginModalOpen: false  // Close login modal if opening register
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load auth data from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    const userData = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);

    if (token && userData) {
      try {
        // ✅ Add token expiry check
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const isExpired = tokenPayload.exp * 1000 < Date.now();
        
        if (isExpired) {
          console.log('Token expired, clearing auth data');
          clearAuthData();
          return;
        }

        const user = JSON.parse(userData);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user, token, refreshToken }
        });
      } catch (error) {
        console.error('Error parsing user data or token:', error);
        clearAuthData();
      }
    }
  }, []);

  // Utility function to clear auth data
  const clearAuthData = () => {
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
  };

  // Utility function to save auth data
  const saveAuthData = (user, token, refreshToken) => {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  };

  const value = {
    state,
    dispatch,
    // Auth actions
    authStart: () => dispatch({ type: 'AUTH_START' }),
        authSuccess: (user, token, refreshToken) => {
      console.log('🎯 AuthContext.authSuccess called with:', { user, token, refreshToken });
      
      try {
        saveAuthData(user, token, refreshToken);
        console.log('💾 Auth data saved to localStorage');
        
        dispatch({ 
          type: 'AUTH_SUCCESS', 
          payload: { user, token, refreshToken } 
        });
        
        // ✅ Add currency sync after login
        if (user?.defaultCurrency && window.setCurrencyAfterLogin) {
          window.setCurrencyAfterLogin(user.defaultCurrency);
        }
        
        console.log('✅ AUTH_SUCCESS dispatch completed');
      } catch (error) {
        console.error('❌ Error in authSuccess:', error);
      }
    },
    authError: (error) => dispatch({ type: 'AUTH_ERROR', payload: error }),
    logout: () => {
      clearAuthData();
      dispatch({ type: 'LOGOUT' });
    },
    setUser: (user) => {
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(user));
      dispatch({ type: 'SET_USER', payload: user });
    },
    refreshTokens: (token, refreshToken) => {
      localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
      localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      dispatch({ 
        type: 'TOKEN_REFRESHED', 
        payload: { token, refreshToken } 
      });
    },
    // Modal actions
    openLoginModal: () => dispatch({ type: 'TOGGLE_LOGIN_MODAL', payload: true }),
    closeLoginModal: () => dispatch({ type: 'TOGGLE_LOGIN_MODAL', payload: false }),
    openRegisterModal: () => dispatch({ type: 'TOGGLE_REGISTER_MODAL', payload: true }),
    closeRegisterModal: () => dispatch({ type: 'TOGGLE_REGISTER_MODAL', payload: false }),
    // Utility functions
    clearAuthData,
    saveAuthData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
